import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Snackbar,
    Alert,
    Pagination,
    Grid,
    Card,
    CardContent,
    CardActions,
    Chip,
    Stack,
    Tooltip,
    Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { API_BASE_URL } from '../types';

interface AuditScan {
    id: number;
    custom_sku: string;
    scan_date: string;
    cover_image_url: string;
    binding_type: string;
    llm_description: string | null;
    match_status: number;
    matched_asin: string | null;
    matched_cover_url: string | null;
    confidence_score: number | null;
    manually_verified: number | null;
    matched_title: string | null;
    matched_author: string | null;
    matched_sales_rank: number | null;
    user_id: string;
    created_at: string;
    updated_at: string;
}

// Track local audit state per card (absence = not yet actioned)
type AuditState = 'approved' | 'rejected';

const ITEMS_PER_PAGE = 30;

const ScanAudit: React.FC = () => {
    const { getToken } = useAuth();
    const [scans, setScans] = useState<AuditScan[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [page, setPage] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

    // Track local audit decisions — cards stay in place, just get an overlay
    const [auditStates, setAuditStates] = useState<Record<string, AuditState>>({});
    // Batch of approved SKUs to flush
    const pendingApprovals = useRef<Set<string>>(new Set());
    const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchScans = useCallback(async (filterDate?: Dayjs | null, pageNum: number = 1) => {
        if (!filterDate) {
            setScans([]);
            setTotalCount(0);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const token = await getToken({ template: 'covervision-backend' });
            if (!token) throw new Error('Authentication token not available.');

            const formattedDate = filterDate.format('YYYY-MM-DD');
            const skip = (pageNum - 1) * ITEMS_PER_PAGE;
            const response = await axios.get<AuditScan[]>(
                `${API_BASE_URL}/scans/scans?match_status=1&audit_pending=true&start_date=${formattedDate}&end_date=${formattedDate}&skip=${skip}&limit=${ITEMS_PER_PAGE}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const total = parseInt(response.headers['x-total-count'] || '0', 10);
            setTotalCount(total);

            if (Array.isArray(response.data)) {
                setScans(response.data);
                // Reset local audit states for new page
                setAuditStates({});
            } else {
                setError('Received unexpected data format from the server.');
                setScans([]);
            }
        } catch (err) {
            console.error('Error fetching scans for audit:', err);
            setError('Failed to fetch scans.');
            setSnackbarMessage('Failed to fetch scans.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        setPage(1);
        fetchScans(selectedDate, 1);
    }, [fetchScans, selectedDate]);

    const handleDateChange = (newDate: Dayjs | null) => {
        setSelectedDate(newDate);
    };

    // Shared POST helper for batch approvals
    const postBatchApprove = useCallback(async (skus: string[], token: string) => {
        await axios.post(
            `${API_BASE_URL}/scans/batch-approve`,
            { skus },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }, []);

    // Flush pending batch approvals to the server
    const flushApprovals = useCallback(async () => {
        if (pendingApprovals.current.size === 0) return;
        const skus = Array.from(pendingApprovals.current);
        pendingApprovals.current.clear();

        try {
            const token = await getToken({ template: 'covervision-backend' });
            if (!token) return;
            await postBatchApprove(skus, token);
        } catch (err) {
            console.error('Error batch approving:', err);
            setAuditStates(prev => {
                const next = { ...prev };
                for (const sku of skus) {
                    if (next[sku] === 'approved') delete next[sku];
                }
                return next;
            });
            setSnackbarMessage('Some approvals failed. Please retry.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    }, [getToken, postBatchApprove]);

    const handleApprove = useCallback((sku: string) => {
        // Instant UI update — card stays in place with green overlay
        setAuditStates(prev => ({ ...prev, [sku]: 'approved' }));

        // Queue for batch send
        pendingApprovals.current.add(sku);

        // Debounce: flush after 800ms of no new approvals
        if (flushTimer.current) clearTimeout(flushTimer.current);
        flushTimer.current = setTimeout(() => {
            flushApprovals();
        }, 800);
    }, [flushApprovals]);

    const handleReject = useCallback(async (sku: string) => {
        setAuditStates(prev => ({ ...prev, [sku]: 'rejected' }));

        try {
            const token = await getToken({ template: 'covervision-backend' });
            if (!token) throw new Error('Authentication token not available.');

            await axios.post(
                `${API_BASE_URL}/scans/${sku}/reject`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbarMessage(`${sku} rejected`);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Error rejecting scan:', err);
            // Revert on failure
            setAuditStates(prev => {
                const next = { ...prev };
                delete next[sku];
                return next;
            });
            setSnackbarMessage('Failed to reject scan.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    }, [getToken]);

    // Approve all visible un-actioned cards
    const handleApproveAll = useCallback(() => {
        const unactioned = scans.filter(s => !auditStates[s.custom_sku]);
        for (const scan of unactioned) {
            handleApprove(scan.custom_sku);
        }
        setSnackbarMessage(`${unactioned.length} scans approved`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    }, [scans, auditStates, handleApprove]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        // Flush any pending approvals before navigating
        if (flushTimer.current) clearTimeout(flushTimer.current);
        flushApprovals();

        setPage(value);
        fetchScans(selectedDate, value);
    };

    // Flush pending approvals on unmount
    useEffect(() => {
        return () => {
            if (flushTimer.current) clearTimeout(flushTimer.current);
            if (pendingApprovals.current.size === 0) return;
            const skus = Array.from(pendingApprovals.current);
            pendingApprovals.current.clear();
            getToken({ template: 'covervision-backend' }).then(token => {
                if (token) postBatchApprove(skus, token).catch(() => {});
            });
        };
    }, [getToken, postBatchApprove]);

    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
    const pendingOnPage = scans.filter(s => !auditStates[s.custom_sku]).length;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Scan Audit
                </Typography>

                {/* Controls */}
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <DatePicker
                                label="Audit Date"
                                value={selectedDate}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        helperText: 'Select a date to audit matched scans',
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="body2" color="textSecondary">
                                {totalCount} total pending &middot; {pendingOnPage} on this page
                            </Typography>
                        </Grid>
                        <Grid item sx={{ ml: 'auto' }}>
                            {scans.length > 0 && pendingOnPage > 0 && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<DoneAllIcon />}
                                    onClick={handleApproveAll}
                                    size="small"
                                >
                                    Approve All ({pendingOnPage})
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : scans.length === 0 ? (
                    <Typography color="textSecondary" sx={{ mt: 4, textAlign: 'center' }}>
                        No scans pending audit for this date.
                    </Typography>
                ) : (
                    <Grid container spacing={3}>
                        {scans.map((scan) => {
                            const state = auditStates[scan.custom_sku];

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={scan.id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            transition: 'opacity 0.3s',
                                            opacity: state ? 0.45 : 1,
                                            pointerEvents: state ? 'none' : 'auto',
                                        }}
                                    >
                                        {/* Audit overlay */}
                                        {state && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 2,
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                {state === 'approved' ? (
                                                    <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', opacity: 0.8 }} />
                                                ) : (
                                                    <CancelIcon sx={{ fontSize: 80, color: 'error.main', opacity: 0.8 }} />
                                                )}
                                            </Box>
                                        )}

                                        {/* Side-by-side images */}
                                        <Box sx={{ display: 'flex', height: 400, backgroundColor: '#f5f5f5' }}>
                                            {/* Scanned cover */}
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e0e0e0' }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        textAlign: 'center',
                                                        py: 0.5,
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#e3f2fd',
                                                    }}
                                                >
                                                    Scanned
                                                </Typography>
                                                <a href={scan.cover_image_url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: 'flex' }}>
                                                    <Box
                                                        component="img"
                                                        src={scan.cover_image_url}
                                                        alt="Scanned cover"
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            p: 1,
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </a>
                                            </Box>
                                            {/* Matched cover */}
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        textAlign: 'center',
                                                        py: 0.5,
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#e8f5e9',
                                                    }}
                                                >
                                                    Matched
                                                </Typography>
                                                {scan.matched_cover_url ? (
                                                    <a href={scan.matched_cover_url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: 'flex' }}>
                                                        <Box
                                                            component="img"
                                                            src={scan.matched_cover_url}
                                                            alt="Matched cover"
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                p: 1,
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </a>
                                                ) : (
                                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography color="textSecondary" variant="body2">No cover</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Scan Details */}
                                        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                            <Stack spacing={1}>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip label={scan.custom_sku} size="small" color="primary" variant="outlined" />
                                                    <Chip label={scan.binding_type} size="small" color="secondary" />
                                                    {scan.confidence_score != null && (
                                                        <Chip
                                                            label={`${(scan.confidence_score * 100).toFixed(1)}%`}
                                                            size="small"
                                                            color={scan.confidence_score >= 0.9 ? 'success' : 'warning'}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {scan.matched_title || scan.llm_description || 'No title'}
                                                </Typography>
                                                {scan.matched_author && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        {scan.matched_author}
                                                    </Typography>
                                                )}
                                                {scan.matched_sales_rank != null && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        Rank: {scan.matched_sales_rank.toLocaleString()}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </CardContent>

                                        {/* Action Buttons */}
                                        <CardActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
                                            <Tooltip title="Approve match">
                                                <IconButton
                                                    color="success"
                                                    onClick={() => handleApprove(scan.custom_sku)}
                                                    size="large"
                                                    disabled={!!state}
                                                >
                                                    <CheckCircleIcon sx={{ fontSize: 36 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject match">
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleReject(scan.custom_sku)}
                                                    size="large"
                                                    disabled={!!state}
                                                >
                                                    <CancelIcon sx={{ fontSize: 36 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {!loading && !error && totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </Box>
                )}

                <Snackbar open={snackbarOpen} autoHideDuration={2000} onClose={() => setSnackbarOpen(false)}>
                    <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </LocalizationProvider>
    );
};

export default ScanAudit;
